<template>
  <div class="app-step-two-tabs-upload">
    <div class="app-step-two-tabs__wrapper">
      <div class="app-step-two-tabs-upload__body">
        <pre v-if="false" class="app-debug">
          <code>
            Loading: {{ isLoading }}
            Form Errors: {{ v$.$errors.length }}
            Uploading Progress: {{ uploadingProgress }}
            FormFields: {{ state }}
          </code>
        </pre>
        <div class="app-step-two-tabs-upload__line"></div>
        <h6 class="app-step-two-tabs-upload__title">Press Release Upload</h6>
        <p class="app-step-two-tabs-upload__descr">
          {{ uploadCaption }}
        </p>

        <upload-release
          v-model="state.releaseContents"
          requestKey="releaseContents"
          :update-order="updateOrder"
          :error="v$.releaseContents.$error"
        ></upload-release>

        <div class="app-step-two-tabs-upload-row step-row">
          <step-input
            v-model="state.prSource"
            :validation-model="v$.prSource"
            label="Your Press Release Source"
            hint="(Usually your company name)"
            tooltipLabel="What is it?"
            tooltipText="PR Newswire requires the source name to appear early in the body of the press release. The source name is the company or person that is sending the press release. If you are an agency or PR firm, this is the actual paying client."
          />
        </div>

        <div class="app-step-two-tabs-upload-row step-row">
          <div class="app-step-two-tabs-upload-row__col step-col">
            <p class="app-step-two-tabs-upload-row__title">
              Document contains dateline (city and state of news origin) & media
              contact info?
            </p>
            <div class="app-step-two-tabs-upload-row__note">
              Note that media contact will be publicly available. Email
              addresses will be anonymized to prevent spam.
            </div>
          </div>
          <div class="app-step-two-tabs-upload-row__col step-col">
            <div class="app-step-two-tabs-upload-row__checkboxes">
              <div class="app-step-two-tabs-upload-row__checkbox">
                <app-checkbox v-model="state.mediaContact.isIncludedInDocument"
                  >Yes</app-checkbox
                >
              </div>
            </div>
          </div>
        </div>

        <div v-show="!state.mediaContact.isIncludedInDocument">
          <div class="app-step-two-tabs-upload-row step-row">
            <step-input
              v-model="state.datelineText"
              :validation-model="v$.datelineText"
              label="Dateline (City & State of Origin)"
            ></step-input>
          </div>
          <div class="app-step-two-tabs-upload-row step-row">
            <step-input
              v-model="state.mediaContact.firstName"
              :validation-model="v$?.mediaContact?.firstName"
              label="Media Contact First Name"
            ></step-input>
          </div>
          <div class="app-step-two-tabs-upload-row step-row">
            <step-input
              v-model="state.mediaContact.lastName"
              :validation-model="v$?.mediaContact?.lastName"
              label="Media Contact Last Name"
            ></step-input>
          </div>
          <div class="app-step-two-tabs-upload-row step-row">
            <step-input
              v-model="state.mediaContact.email"
              :validation-model="v$?.mediaContact?.email"
              label="Media Contact Email"
            ></step-input>
          </div>
          <div class="app-step-two-tabs-upload-row step-row">
            <step-input
              label="Media Contact Phone"
              v-model="state.mediaContact.phone"
              :validation-model="v$?.mediaContact?.phone"
            ></step-input>
          </div>
        </div>
      </div>
    </div>

    <navigation-buttons
      :disabled-outer-condition="isLoading || isZeroWordCount"
      :loading="isLoading"
      @onPreviousButtonClick="handlePreviousClick"
      @onNextButtonClick="handleNextClick"
    ></navigation-buttons>
  </div>
</template>

<script setup>
import _ from 'lodash-es'
import NavigationButtons from '@modules/app-steps/step-two/navigation-buttons/index.vue'
import AppCheckbox from '@modules/ui/app-checkbox.vue'
import currency from 'currency.js'

import StepInput from '@modules/app-steps/step-input.vue'
import useVuelidate from '@vuelidate/core'
import UploadRelease from '@modules/app-steps/step-two/tabs/upload/upload-release/index.vue'

import {
  required,
  numeric,
  maxLength,
  email,
  minLength,
} from '@vuelidate/validators'
import { useOrder } from '@core/composables/order/useOrder'
import { useNavigationButtons } from '@modules/app-steps/step-two/navigation-buttons'
import { computed } from 'vue'
import { state } from './state'
import { useTabsStore } from '@/stores/tabs'

const { getFrontendTabBuilder } = useTabsStore()

const { navigate } = useNavigationButtons()

const { order, useUpdateOrder } = useOrder()
const { isLoading, updateOrder, uploadingProgress } = useUpdateOrder()

const rules = computed(() => {
  const localRules = {
    releaseContents: { name: { required } },
    prSource: { required, maxLength: maxLength(80) },
  }

  if (!state.mediaContact.isIncludedInDocument) {
    localRules.datelineText = { required, maxLength: maxLength(80) }
    localRules.mediaContact = {
      firstName: {
        required,
        maxLength: maxLength(100),
      },
      lastName: {
        required,
        maxLength: maxLength(100),
      },
      email: {
        required,
        email,
        maxLength: maxLength(100),
      },
      phone: {
        required,
        minLength: minLength(10),
        maxLength: maxLength(25),
      },
    }
  }
  return localRules
})

const uploadCaption = computed(() => {
  const { wordCount, additionalWordsPrice } =
    order.value?.packagePrice?.package ?? {}

  if (wordCount == null) {
    return 'Select a package to see word limits and pricing for this release. Press contact including a person\'s name and telephone number is required to be issued to the wires.'
  }

  return `
  ${wordCount} words maximum including headline & contact;
  Each additional block of up to 100 words costs ${currency(
    additionalWordsPrice
  ).format()}. Press contact including a person's name and telephone number is required to be issued to the wires.
  `
})

const wordCount = computed(() => order?.value?.pressReleaseContent?.wordCount)
const isZeroWordCount = computed(() => wordCount.value === 0)

const v$ = useVuelidate(rules, state)

const handlePreviousClick = () => {
  updateOrder(
    {
      ...getFrontendTabBuilder(),
    },
    () => navigate()
  )
}

const handleNextClick = async () => {
  const errors = !(await v$.value.$validate())
  if (errors) return

  const payload = {
    prSource: state.prSource,
  }

  if (!state.mediaContact.isIncludedInDocument) {
    const { firstName, lastName, email, phone } = state.mediaContact
    payload.mediaContact = { firstName, lastName, email, phone }
    payload.datelineText = state.datelineText
  } else {
    payload.mediaContact = { isIncludedInDocument: true }
  }

  updateOrder(
    {
      ...payload,
      ...getFrontendTabBuilder(true),
    },
    () => navigate(true)
  )
}
</script>
